import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import AppTable from "components/AppTable/AppTable";
import { expiredInDays, toDate } from "helpers/dateHelper";
import "./contacts.scss";
import _ from "lodash";
import Pagination from "components/Pagination/Pagination";
import { useState } from "react";
import { useGetContacts } from "hooks/useGetContacts";
import {
  FaCalendarAlt,
  FaGlobe,
  FaTelegram,
  FaInstagram,
  FaUserAlt,
  FaWhatsapp,
  FaUser,
  FaTrash,
  FaTags,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import contactsService from "services/admin/contactsService";
import { toast } from "react-toastify";
import { queryClient } from "queryClient";
import withRouter from "hoc/withRouter";

const ContactList = ({ router }: { router: any }) => {
  const page = router.queryParams.get("page") || 1;
  const limit = Number(router.queryParams.get("limit")) || 100;
  const search = router.queryParams.get("search") || "";
  const sort = router.queryParams.get("sort") || "createdAt";
  const order = router.queryParams.get("order") || "desc";
  const filter = router.queryParams.get("filter") || "";
  const filterValue = router.queryParams.get("filterValue") || "";
  const filterOperator = router.queryParams.get("filterOperator") || "=";

  const [query, setQuery] = useState({
    page,
    limit,
    search,
    sort,
    order,
    filter,
    filterValue,
    filterOperator,
  });

  const columnConfig = [
    // Photo
    {
      key: "externalUserPhoto",
      label: "Фото",
      render: (c: any) => (
        <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center" onClick={() => setContact(c)}>
          {c?.externalUserPhoto ? (
            <img
              src={c?.externalUserPhoto}
              alt="User Photo"
              className="w-full h-full object-cover"
            />
          ) : (
            <FaUser size={20} />
          )}
        </div>
      ),
    },
    {
      key: "externalUserFirstName",
      label: "Имя контакта",
      render: (c: any) => (
        <div className="color-blue cursor-pointer" onClick={() => setContact(c)}>
          {c?.externalUserFirstName || c?.externalUserUserName || c?.externalUserID}
        </div>
      ),
    },
    // Platform
    {
      key: "platform",
      label: "Платформа",
      render: (c: any) => (
        <div className="text-sm flex items-center gap-2 justify-center">
          {c.platform === "WHATSAPP" ? (
            <FaWhatsapp size={20} style={{ color: "#25D366" }} />
          ) : c.platform === "INSTAGRAM" ? (
            <FaInstagram size={20} style={{ color: "#C13584" }} />
          ) : c.platform === "TELEGRAM" ? (
            <FaTelegram size={20} style={{ color: "#0088cc" }} />
          ) : (
            <FaGlobe size={20} style={{ color: "#FFA500" }} />
          )}
        </div>
      ),
    },
    {
      key: "externalUserID",
      label: "Аккаунт",
      render: (c: any) => (
        <div className="color-blue cursor-pointer" onClick={() => setContact(c)}>
          {_.get(c, "externalUserUserName", "-")}
        </div>
      ),
    },
    {
      key: "subscribed",
      label: "Подписан",
      render: (c: any) => (
        <div className="text-sm">
          {c.isUserFollowBusiness ? "Да" : "Нет"}
        </div>
      ),
    },
    {
      key: "createdAt",
      label: "Дата добавления",
      formatter: (v: any) => toDate(v),
    },
    {
      key: "replyExpires",
      label: "Статус",
      render: (i: any) => {
        if (!i.replyExpires || (i.replyExpires && new Date(i.replyExpires) < new Date())) {
          return <Tooltip title={`Не может получать автоматизированные сообщения! Срок истек ${toDate(i.replyExpires)}`}>
            <IconButton size="small" color="error"><FaExclamationTriangle size={14} /></IconButton>
          </Tooltip>
        }
        return <Tooltip title={`Может получать автоматизированные сообщения до ${toDate(i.replyExpires)}`}>
          <IconButton size="small" color="success"><FaCheckCircle size={14} /></IconButton>
        </Tooltip>
      },
    }
  ];

  const { data, isLoading } = useGetContacts(query);
  console.log(`Contacts: `, data);
  const [contact, setContact] = useState<any>(null);
  const navigate = useNavigate();

  const handleClose = () => {
    setContact(null);
  };

  const handleDelete = async (id: string) => {
    try {
      if (
        window.confirm(
          "Вы уверены, что хотите удалить контакт? Удаление контакта приведет к удалению всех чатов с этим контактом."
        )
      ) {
        await contactsService.delete(id);

        queryClient.setQueryData(["contacts", query], (old: any) => {
          return {
            ...old,
            rows: old?.rows?.filter((c: any) => c.id != id),
            count: old?.count - 1,
          };
        });
        toast.success("Контакт удален");
        setContact(null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Ошибка при удалении контакта");
    }
  };

  const onPageChange = (page: number) => {
    setQuery({
      ...query,
      page,
    });

    window.history.pushState(null, "", `/admin/contacts?page=${page}`);
  };

  return isLoading ? (
    <div className="text-center">
      <CircularProgress></CircularProgress>
    </div>
  ) : (
    <div>
      <h3 className="m-b-16">Контакты</h3>
      {data?.count > 0 ? (
        <AppTable data={data?.rows || []} columnConfig={columnConfig as any}></AppTable>
      ) : (
        <div className="text-center">
          <span className="text-sm">Контакты отсутствуют</span>
        </div>
      )}
      {data && data?.count > limit && (
        <Pagination
          total={data?.count}
          limit={Number(limit)}
          page={Number(page)}
          onPageChange={(page: number) => {
            onPageChange(page);
          }}
        />
      )}
      <Dialog open={!!contact} onClose={handleClose} fullWidth>
        <DialogContent>
          <div>
            <div className="flex items-center justify-center mb-4">
              {contact?.externalUserPhoto ? (
                <div className="w-30 h-30 rounded-full overflow-hidden flex items-center justify-center">
                  <img
                    src={contact?.externalUserPhoto}
                    alt="User Photo"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                  <FaUser size={20} />
                </div>
              )}
            </div>
            <h3 className="text-2xl font-bold text-center">{contact?.externalUserFirstName}</h3>
            <div className="flex items-center gap-2 mb-2">
              <FaUserAlt />
              <span className="text-sm ">{contact?.externalUserUserName || contact?.externalUserID}</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <FaCalendarAlt />
              <span className="text-sm ">{toDate(contact?.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <FaGlobe />
              <span className="text-sm ">{contact?.platform}</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <FaCheckCircle />
              <span className="text-sm" style={{ color: contact?.isUserFollowBusiness ? "green" : "red" }}>{contact?.isUserFollowBusiness ? "Подписан" : "Не подписан"}</span>
            </div>
            <div className="flex items-center gap-2 mb-2 color-blue">
              {contact?.platform === "WHATSAPP" && (
                <div className="flex items-center gap-2">
                  <FaWhatsapp />
                  <a
                    href={`https://wa.me/${contact?.externalUserUserName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="text-sm">{contact?.externalUserUserName}</span>
                  </a>
                </div>
              )}
              {contact?.platform === "INSTAGRAM" && (
                <div className="flex items-center gap-2 mb-2">
                  <FaInstagram />
                  <a
                    href={`https://www.instagram.com/${contact?.externalUserUserName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="text-sm">{contact?.externalUserUserName}</span>
                  </a>
                </div>
              )}
              {contact?.platform === "TELEGRAM" && (
                <div className="flex items-center gap-2 mb-2">
                  <FaTelegram />
                  <a
                    href={`https://t.me/${contact?.externalUserUserName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="text-sm">{contact?.externalUserUserName}</span>
                  </a>
                </div>
              )}
              {contact?.platform === "TEST_SANDBOX" && (
                <div className="flex items-center gap-2 mb-2">
                  <FaGlobe />
                  <a href={`https://www.google.com`} target="_blank" rel="noopener noreferrer">
                    <span className="text-sm">{contact?.externalUserUserName}</span>
                  </a>
                </div>
              )}
            </div>
            {/* Tags */}
            <div className="flex items-center gap-2 mb-2">
              <FaTags />
              <span className="text-sm ">{contact?.tags?.length > 0 ? contact?.tags.map((t: any) => t).join(", ") : "Отсутствуют"}</span>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="medium"
            onClick={() => {
              navigate(`/admin/chats?contactID=${contact?.id}`);
            }}
          >
            Начать чат
          </Button>
          {/* Delete button */}
          <Button
            variant="outlined"
            color="error"
            size="medium"
            onClick={() => {
              handleDelete(contact?.id);
            }}
          >
            <FaTrash /> Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withRouter(ContactList);
